import { Controller } from "@hotwired/stimulus"

/**
 * Controller for managing RTQ Past Rates table with add/delete columns and auto-save.
 *
 * Usage:
 *   <div data-controller="rtq-past-rates"
 *        data-rtq-past-rates-create-url-value="/path/to/create"
 *        data-rtq-past-rates-max-columns-value="3"
 *        data-rtq-past-rates-extract-url-value="/path/to/extract"
 *        data-rtq-past-rates-extraction-status-url-value="/path/to/extraction_status"
 *        data-rtq-past-rates-extracting-value="false">
 *
 * Dropping files on the card (or choosing them with the fileInput target) uploads them for past rate
 * extraction, then polls until the extractions finish and reloads to show the filled rates.
 */
export default class extends Controller {
  static targets = ["column", "addButton", "status", "fileInput"]

  static values = {
    createUrl: String,
    extractUrl: String,
    extractionStatusUrl: String,
    extracting: Boolean,
    maxColumns: { type: Number, default: 3 },
    debounce: { type: Number, default: 500 },
    pollInterval: { type: Number, default: 3000 }
  }

  connect() {
    this.debounceTimers = {}
    this.updateAddButtonVisibility()
    if (this.extractingValue) this.pollExtractionStatus()
  }

  disconnect() {
    Object.values(this.debounceTimers).forEach(timer => clearTimeout(timer))
    clearTimeout(this.pollTimer)
  }

  dragOver(event) {
    if (!this.isFileDrag(event)) return
    event.preventDefault()
    this.element.classList.add("ring-2", "ring-blue-500", "bg-blue-50")
  }

  dragLeave(event) {
    if (this.element.contains(event.relatedTarget)) return
    this.element.classList.remove("ring-2", "ring-blue-500", "bg-blue-50")
  }

  drop(event) {
    if (!this.isFileDrag(event)) return
    event.preventDefault()
    this.element.classList.remove("ring-2", "ring-blue-500", "bg-blue-50")
    this.uploadFiles([...event.dataTransfer.files])
  }

  isFileDrag(event) {
    return [...event.dataTransfer.types].includes("Files")
  }

  selectFiles() {
    this.fileInputTarget.click()
  }

  filesSelected(event) {
    this.uploadFiles([...event.target.files])
    event.target.value = ""
  }

  async uploadFiles(files) {
    if (files.length === 0) return

    const formData = new FormData()
    files.forEach(file => formData.append("files[]", file))

    this.showStatus("Uploading...")

    try {
      const response = await fetch(this.extractUrlValue, {
        method: "POST",
        headers: {
          "Accept": "application/json",
          "X-CSRF-Token": this.csrfToken
        },
        body: formData
      })

      const data = await response.json()

      if (response.ok && data.status === "success") {
        // Reload so the card lists the new extractions; polling resumes from the server state.
        window.location.reload()
      } else {
        this.showStatus("Error uploading", true)
      }
    } catch (error) {
      console.error("Error uploading past rate documents:", error)
      this.showStatus("Network error", true)
    }
  }

  pollExtractionStatus() {
    this.pollTimer = setTimeout(async () => {
      try {
        const response = await fetch(this.extractionStatusUrlValue, { headers: { "Accept": "application/json" } })
        const data = await response.json()

        if (response.ok && data.pending === 0) {
          this.reloadWhenSaved()
          return
        }
      } catch (error) {
        console.error("Error checking extraction status:", error)
      }
      this.pollExtractionStatus()
    }, this.pollIntervalValue)
  }

  // Avoid losing an edit that is still waiting to be auto-saved.
  reloadWhenSaved() {
    if (Object.keys(this.debounceTimers).length > 0 || this.saving > 0) {
      this.pollTimer = setTimeout(() => this.reloadWhenSaved(), 500)
    } else {
      window.location.reload()
    }
  }

  updateAddButtonVisibility() {
    if (this.hasAddButtonTarget) {
      const columnCount = this.columnTargets.length
      this.addButtonTarget.classList.toggle("hidden", columnCount >= this.maxColumnsValue)
    }
  }

  async addColumn(event) {
    event.preventDefault()

    const columnCount = this.columnTargets.length
    if (columnCount >= this.maxColumnsValue) return

    this.showStatus("Adding...")

    try {
      const response = await fetch(this.createUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": this.csrfToken
        },
        body: JSON.stringify({ rtq_past_rate: {} })
      })

      const data = await response.json()

      if (response.ok && data.status === "success") {
        // Reload the page to show the new column
        window.location.reload()
      } else {
        this.showStatus("Error adding", true)
      }
    } catch (error) {
      console.error("Error adding past rate:", error)
      this.showStatus("Network error", true)
    }
  }

  async deleteColumn(event) {
    event.preventDefault()

    if (!confirm("Are you sure you want to delete this past rate?")) return

    const button = event.currentTarget
    const url = button.dataset.url

    this.showStatus("Deleting...")

    try {
      const response = await fetch(url, {
        method: "DELETE",
        headers: {
          "Accept": "application/json",
          "X-CSRF-Token": this.csrfToken
        }
      })

      const data = await response.json()

      if (response.ok && data.status === "success") {
        window.location.reload()
      } else {
        this.showStatus("Error deleting", true)
      }
    } catch (error) {
      console.error("Error deleting past rate:", error)
      this.showStatus("Network error", true)
    }
  }

  fieldChanged(event) {
    const input = event.target
    const url = input.dataset.url
    const field = input.dataset.field

    if (!url || !field) return

    // Clear existing timer for this input
    const timerId = `${url}-${field}`
    if (this.debounceTimers[timerId]) {
      clearTimeout(this.debounceTimers[timerId])
    }

    this.showStatus("Saving...")

    this.debounceTimers[timerId] = setTimeout(() => {
      delete this.debounceTimers[timerId]
      this.saveField(url, field, input.value)
    }, this.debounceValue)
  }

  async saveField(url, field, value) {
    this.saving = (this.saving || 0) + 1
    try {
      const response = await fetch(url, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": this.csrfToken
        },
        body: JSON.stringify({ rtq_past_rate: { [field]: value } })
      })

      const data = await response.json()

      if (response.ok && data.status === "success") {
        this.showStatus("Saved", false, 2500)
      } else {
        this.showStatus("Error saving", true)
      }
    } catch (error) {
      console.error("Error saving field:", error)
      this.showStatus("Network error", true)
    } finally {
      this.saving -= 1
    }
  }

  showStatus(message, isError = false, autoClearMs = 0) {
    if (!this.hasStatusTarget) return

    const statusClass = isError ? "text-red-600" : "text-gray-400"
    this.statusTarget.innerHTML = `<span class="${statusClass}">${message}</span>`

    if (autoClearMs > 0) {
      setTimeout(() => {
        this.statusTarget.innerHTML = ""
      }, autoClearMs)
    }
  }

  get csrfToken() {
    return document.querySelector("meta[name='csrf-token']")?.content
  }
}
